import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Upload, FileText, Trash2, BookOpen, Zap as ZapIcon, AlertOctagon,
  X, AlertTriangle, Wrench, ChevronRight, ChevronDown, Package,
} from 'lucide-react';

const DOC_TYPES = (t) => [
  { value: 'manuale', label: t('documents.type_manual'), icon: BookOpen },
  { value: 'schema_elettrico', label: t('documents.type_schema'), icon: ZapIcon },
  { value: 'codici_errore', label: t('documents.type_alarms'), icon: AlertOctagon },
];

// Type → section key (used for the customer → vehicle → section hierarchy)
const DOC_TYPE_SECTION = {
  manuale: 'manuals',
  schema_elettrico: 'schematics',
  codici_errore: 'alarms',
  ricambi: 'spare_parts', // legacy spare-parts via /documents upload
};

const SECTION_META = (t) => ({
  manuals: { icon: BookOpen, label: t('documents_tree.section_manuals') },
  schematics: { icon: ZapIcon, label: t('documents_tree.section_schematics') },
  alarms: { icon: AlertOctagon, label: t('documents_tree.section_alarms') },
  spare_parts: { icon: Package, label: t('documents_tree.section_spare_parts') },
  other: { icon: FileText, label: t('documents_tree.section_other') },
});

// Normalize brand/customer for grouping: lowercase, strip accents, collapse non-alnum.
function normalizeKey(s) {
  if (!s) return '';
  return s
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();
}

function pickCanonicalDisplay(variants) {
  const counts = {};
  for (const v of variants) counts[v] = (counts[v] || 0) + 1;
  const sorted = Object.entries(counts).sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    const isMixed = (s) => /[A-Z]/.test(s) && /[a-z]/.test(s);
    if (isMixed(b[0]) !== isMixed(a[0])) return isMixed(b[0]) ? 1 : -1;
    return a[0].localeCompare(b[0]);
  });
  return sorted[0][0];
}

export default function DocumentsPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const docTypes = DOC_TYPES(t);
  const sectionMeta = SECTION_META(t);

  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ brand: '', model: '', client_name: '', doc_type: 'manuale', file: null });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  // All tree nodes start COLLAPSED. State: {nodeId: true} when expanded.
  const [expanded, setExpanded] = useState({});

  const load = () => api.get('/documents').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  // Build the tree: brand (manufacturer) → vehicle (model) → section → docs.
  // Customer name is shown as a small badge on each document, not as a grouping level.
  const tree = useMemo(() => {
    const brands = new Map();
    for (const d of items) {
      const brandRaw = d.brand || '—';
      const bKey = normalizeKey(brandRaw) || '__unknown__';
      const b = brands.get(bKey) || { key: bKey, brandVariants: [], vehicles: new Map() };
      b.brandVariants.push(brandRaw);

      const modelRaw = d.model || '—';
      const vKey = normalizeKey(modelRaw) || '__nomodel__';
      const v = b.vehicles.get(vKey) || { key: vKey, modelVariants: [], sections: new Map() };
      v.modelVariants.push(modelRaw);

      const section = DOC_TYPE_SECTION[d.doc_type] || 'other';
      const arr = v.sections.get(section) || [];
      arr.push(d);
      v.sections.set(section, arr);

      b.vehicles.set(vKey, v);
      brands.set(bKey, b);
    }

    return Array.from(brands.values()).map((b) => ({
      ...b,
      brandDisplay: pickCanonicalDisplay(b.brandVariants),
      vehicles: Array.from(b.vehicles.values()).map((v) => ({
        ...v,
        modelDisplay: pickCanonicalDisplay(v.modelVariants),
        sections: Array.from(v.sections.entries()).map(([sec, docs]) => ({
          key: sec,
          docs: docs.slice().sort((a, b2) => (a.filename || '').localeCompare(b2.filename || '')),
        })).sort((a, b2) => a.key.localeCompare(b2.key)),
      })).sort((a, b2) => a.modelDisplay.localeCompare(b2.modelDisplay, undefined, { sensitivity: 'base' })),
    })).sort((a, b2) => a.brandDisplay.localeCompare(b2.brandDisplay, undefined, { sensitivity: 'base' }));
  }, [items]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // Auto-refresh while any doc is processing
  useEffect(() => {
    const hasProcessing = items.some((d) => d.status === 'processing');
    if (!hasProcessing) return;
    const handle = setInterval(load, 3000);
    return () => clearInterval(handle);
  }, [items]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error('Seleziona un file');
    const fd = new FormData();
    fd.append('file', form.file);
    fd.append('brand', form.brand);
    fd.append('model', form.model);
    fd.append('doc_type', form.doc_type);
    if (form.client_name) fd.append('client_name', form.client_name);
    setUploading(true);
    try {
      await api.post('/documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      toast.success('Documento caricato — indicizzazione in corso');
      setForm({ brand: '', model: '', client_name: '', doc_type: 'manuale', file: null });
      const fi = document.getElementById('pdf-file');
      if (fi) fi.value = '';
      load();
    } catch (err) {
      const msg = err?.code === 'ECONNABORTED'
        ? 'Timeout: il file è troppo grande o il server è lento.'
        : (err?.response?.data?.detail || err?.message || 'Errore caricamento');
      toast.error(msg);
    } finally { setUploading(false); setProgress(0); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${confirmDelete.id}`);
      toast.success('Documento eliminato');
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    } finally { setDeleting(false); }
  };

  const expandAll = () => {
    const next = {};
    tree.forEach((b) => {
      next[`b:${b.key}`] = true;
      b.vehicles.forEach((v) => {
        next[`v:${b.key}:${v.key}`] = true;
        v.sections.forEach((s) => { next[`s:${b.key}:${v.key}:${s.key}`] = true; });
      });
    });
    setExpanded(next);
  };
  const collapseAll = () => setExpanded({});

  const getTypeMeta = (typeVal) => docTypes.find((x) => x.value === typeVal) || docTypes[0];

  return (
    <div className="animate-fade-up">
      <div className="overline text-[#002FA7]">KNOWLEDGE BASE</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">{t('documents.title')}</h1>
      <p className="text-gray-500 mt-2">Manuali, schemi elettrici, codici errore, cataloghi ricambi — organizzati per produttore e modello.</p>

      {user?.role !== 'admin' && (
        <div className="mt-6 border border-yellow-300 bg-yellow-50 p-4 text-sm flex items-start gap-3" data-testid="non-admin-notice">
          <AlertTriangle className="w-5 h-5 text-yellow-700 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-gray-900">Permessi limitati</div>
            <div className="text-gray-700">Solo gli amministratori possono caricare o eliminare documenti tecnici. Sei loggato come <span className="font-mono">{user?.role}</span>.</div>
          </div>
        </div>
      )}

      {user?.role === 'admin' && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowUpload((v) => !v)}
            className="btn-secondary"
            data-testid="upload-toggle"
          >
            {showUpload ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {t('documents.upload')}
          </button>
          {showUpload && (
            <form onSubmit={submit} className="surface p-5 mt-3" data-testid="upload-form">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('machines.client_name')}</label>
                  <input className="input-base" value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    placeholder="es. Acme S.p.A." data-testid="doc-client" />
                </div>
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('machines.brand')} *</label>
                  <input required className="input-base" value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    placeholder="es. Dulevo" data-testid="doc-brand" />
                </div>
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('machines.model')} *</label>
                  <input required className="input-base" value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    placeholder="es. D.Zero²" data-testid="doc-model" />
                </div>
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('documents.type')}</label>
                  <select className="input-base" value={form.doc_type}
                    onChange={(e) => setForm({ ...form, doc_type: e.target.value })} data-testid="doc-type">
                    {docTypes.map((dt) => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label className="overline text-gray-600 block mb-1">{t('documents.filename')} *</label>
                <input id="pdf-file" type="file" accept=".pdf,.xlsx,.xls,.xlsm,.docx,.csv,.txt" required
                  onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                  className="input-base py-1.5 text-xs" data-testid="doc-file" />
                <div className="text-[0.65rem] text-gray-500 mt-1 font-mono">PDF · XLSX · XLS · DOCX · CSV · TXT</div>
              </div>
              <div className="mt-4 flex justify-end items-center gap-3">
                {uploading && progress > 0 && progress < 100 && (
                  <div className="text-xs text-gray-500 font-mono" data-testid="upload-progress">Upload: {progress}%</div>
                )}
                {uploading && progress === 100 && (
                  <div className="text-xs text-blue-700 font-mono">Estrazione testo in corso…</div>
                )}
                <button type="submit" disabled={uploading} className="btn-primary" data-testid="upload-btn">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Indicizzazione…' : t('documents.upload')}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 justify-end" data-testid="tree-controls">
        <button type="button" onClick={expandAll} className="text-xs text-gray-600 hover:text-[#002FA7] underline-offset-2 hover:underline" data-testid="expand-all">
          {t('documents_tree.expand_all')}
        </button>
        <span className="text-gray-300">·</span>
        <button type="button" onClick={collapseAll} className="text-xs text-gray-600 hover:text-[#002FA7] underline-offset-2 hover:underline" data-testid="collapse-all">
          {t('documents_tree.collapse_all')}
        </button>
      </div>

      <div className="mt-2 space-y-3" data-testid="documents-tree">
        {items.length === 0 ? (
          <div className="surface p-12 text-center">
            <FileText className="w-10 h-10 text-gray-300 mx-auto" strokeWidth={1.2} />
            <div className="mt-3 font-display font-bold text-lg">{t('documents.no_documents')}</div>
          </div>
        ) : tree.map((b) => {
          const bId = `b:${b.key}`;
          const bOpen = !!expanded[bId];
          const totalDocs = b.vehicles.reduce((acc, v) => acc + v.sections.reduce((a, s) => a + s.docs.length, 0), 0);
          return (
            <div key={b.key} className="surface overflow-hidden" data-testid={`brand-group-${b.key}`}>
              <button
                type="button"
                onClick={() => toggle(bId)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 text-left"
                data-testid={`brand-header-${b.key}`}
              >
                {bOpen ? <ChevronDown className="w-4 h-4 text-[#002FA7]" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <Wrench className="w-4 h-4 text-[#002FA7]" />
                <span className="font-display font-bold uppercase tracking-wide">{b.brandDisplay}</span>
                <span className="ml-auto text-xs text-gray-500 font-mono">{totalDocs}</span>
              </button>
              {bOpen && (
                <div className="divide-y divide-gray-200">
                  {b.vehicles.map((v) => {
                    const vId = `v:${b.key}:${v.key}`;
                    const vOpen = !!expanded[vId];
                    return (
                      <div key={v.key} data-testid={`vehicle-group-${b.key}-${v.key}`}>
                        <button
                          type="button"
                          onClick={() => toggle(vId)}
                          className="w-full flex items-center gap-3 px-6 py-2.5 bg-white hover:bg-gray-50 text-left"
                          data-testid={`vehicle-header-${b.key}-${v.key}`}
                        >
                          {vOpen ? <ChevronDown className="w-4 h-4 text-[#002FA7]" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          <Package className="w-4 h-4 text-gray-700" />
                          <span className="font-mono text-sm font-semibold text-gray-800">{v.modelDisplay}</span>
                          <span className="ml-auto text-xs text-gray-500 font-mono">
                            {v.sections.reduce((a, s) => a + s.docs.length, 0)}
                          </span>
                        </button>
                        {vOpen && (
                          <div className="bg-gray-50/50">
                            {v.sections.map((s) => {
                              const sId = `s:${b.key}:${v.key}:${s.key}`;
                              const sOpen = !!expanded[sId];
                              const meta = sectionMeta[s.key] || sectionMeta.other;
                              const SectionIcon = meta.icon;
                              return (
                                <div key={s.key} data-testid={`section-group-${b.key}-${v.key}-${s.key}`}>
                                  <button
                                    type="button"
                                    onClick={() => toggle(sId)}
                                    className="w-full flex items-center gap-3 px-10 py-2 hover:bg-white text-left text-sm"
                                    data-testid={`section-header-${b.key}-${v.key}-${s.key}`}
                                  >
                                    {sOpen ? <ChevronDown className="w-3.5 h-3.5 text-[#002FA7]" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                                    <SectionIcon className="w-3.5 h-3.5 text-gray-600" />
                                    <span className="text-gray-700">{meta.label}</span>
                                    <span className="ml-auto text-xs text-gray-500 font-mono">{s.docs.length}</span>
                                  </button>
                                  {sOpen && (
                                    <div className="divide-y divide-gray-200 bg-white">
                                      {s.docs.map((d) => {
                                        const tMeta = getTypeMeta(d.doc_type);
                                        const TypeIcon = tMeta.icon;
                                        return (
                                          <div key={d.id} className="px-12 py-3 flex items-center justify-between gap-3 hover:bg-gray-50" data-testid={`doc-${d.id}`}>
                                            <div className="flex items-start gap-3 min-w-0">
                                              <TypeIcon className="w-4 h-4 text-gray-600 mt-0.5 shrink-0" strokeWidth={1.8} />
                                              <div className="min-w-0">
                                                <div className="font-mono text-sm truncate">{d.filename}</div>
                                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                                                  <span>{d.pages} {t('documents.pages')}</span>
                                                  <span>· {d.size_kb} KB</span>
                                                  {d.client_name && <span>· {d.client_name}</span>}
                                                  {d.status && d.status !== 'ready' && (
                                                    <span className={d.status === 'error' ? 'text-red-600' : 'text-blue-700'}>· {t(`documents.status_${d.status}`)}</span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            {user?.role === 'admin' && (
                                              <button onClick={() => setConfirmDelete(d)}
                                                className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-red-700 active:scale-95 transition-all shrink-0"
                                                data-testid={`delete-doc-${d.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 animate-fade-up"
          onClick={() => !deleting && setConfirmDelete(null)} data-testid="confirm-delete-modal">
          <div className="bg-white border border-gray-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200 flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg">Eliminare il documento?</div>
                <div className="text-sm text-gray-600 mt-1 font-mono">{confirmDelete.filename}</div>
                <div className="text-xs text-gray-500 mt-2">Verranno rimossi anche tutti i chunk indicizzati. L'azione è irreversibile.</div>
              </div>
              <button onClick={() => !deleting && setConfirmDelete(null)} className="btn-ghost shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} disabled={deleting} className="btn-secondary" data-testid="confirm-cancel">
                {t('app.cancel')}
              </button>
              <button onClick={remove} disabled={deleting}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 font-semibold hover:bg-red-700 active:scale-95 transition-all disabled:opacity-50"
                data-testid="confirm-delete">
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Eliminazione…' : t('app.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
